import { ApplicationRef, computed, inject, Injector } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, exhaustMap, filter, from, map, pipe, switchMap, take, tap, timer } from 'rxjs';
import { ConfirmationDialogService, isConfirmed } from '@v1/core/confirmation/confirmation-dialog.service';

const UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1_000;

interface ConfigurationUpdateState {
  enabled: boolean;
  checking: boolean;
  applying: boolean;
  available: boolean;
  checked: boolean;
  error: string | null;
}

const initialState: ConfigurationUpdateState = {
  enabled: false,
  checking: false,
  applying: false,
  available: false,
  checked: false,
  error: null,
};

export const ConfigurationUpdateStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    canCheck: computed(() => store.enabled() && !store.checking() && !store.applying()),
    canApply: computed(() => store.enabled() && store.available() && !store.applying()),
  })),
  withMethods((store, updates = inject(SwUpdate), confirmation = inject(ConfirmationDialogService)) => {
    const checkForUpdate = rxMethod<void>(pipe(
      filter(() => store.enabled() && !store.checking() && !store.applying()),
      tap(() => patchState(store, { checking: true, error: null })),
      exhaustMap(() => from(updates.checkForUpdate()).pipe(
        tap((available) => patchState(store, { checked: true, available: available || store.available() })),
        catchError(() => {
          patchState(store, { error: 'No fue posible comprobar las actualizaciones.' });
          return EMPTY;
        }),
        tap({ finalize: () => patchState(store, { checking: false }) }),
      )),
    ));

    const applyUpdate = rxMethod<void>(pipe(
      filter(() => store.canApply()),
      exhaustMap(() => confirmation.confirm({
        header: 'Aplicar actualización',
        message: 'La aplicación se recargará para usar la nueva versión. ¿Deseas continuar?',
        acceptLabel: 'Actualizar',
      }).pipe(
        filter(isConfirmed),
        filter(() => store.canApply()),
        tap(() => patchState(store, { applying: true, error: null })),
        exhaustMap(() => from(updates.activateUpdate()).pipe(
          tap((updated) => {
            if (updated) window.location.reload();
            else patchState(store, { available: false, checked: true });
          }),
          catchError(() => {
            patchState(store, { error: 'No fue posible aplicar la actualización.' });
            return EMPTY;
          }),
          tap({ finalize: () => patchState(store, { applying: false }) }),
        )),
      )),
    ));

    const listenVersionReady = rxMethod<VersionEvent>(pipe(
      filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
      tap((event) => {
        if (event.currentVersion.hash !== event.latestVersion.hash) {
          patchState(store, { available: true, checked: true, checking: false, error: null });
        }
      }),
    ));

    const scheduleChecks = rxMethod<void>(pipe(
      switchMap(() => timer(0, UPDATE_CHECK_INTERVAL_MS)),
      tap(() => checkForUpdate()),
    ));

    return { checkForUpdate, applyUpdate, listenVersionReady, scheduleChecks };
  }),
  withHooks((store, applicationRef = inject(ApplicationRef), updates = inject(SwUpdate), injector = inject(Injector)) => ({
    onInit(): void {
      patchState(store, { enabled: updates.isEnabled });
      if (!updates.isEnabled) return;
      store.listenVersionReady(updates.versionUpdates, { injector });
      store.scheduleChecks(
        applicationRef.isStable.pipe(
          filter((stable): stable is true => stable),
          take(1),
          map(() => undefined),
        ),
        { injector },
      );
    },
  })),
);
